import React from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select, Switch } from 'antd';
import { useQuery } from '@common/hooks';
import { ErrorHandler, LoadingState } from '@common/components';
import {
  AssessmentTypes,
  IssuerList,
} from 'works/graphql/works-list-item-template';
import ExpirationTypeSelect from './expiration-type-select';

export default function FormAssessmentTemplate({
  handleSubmit,
  initialValues,
  showExpiration,
  form,
  currentAssessment,
  currentIssuers = [],
}) {
  const { loading, error, data } = useQuery(AssessmentTypes);
  const {
    loading: issuerLoading,
    error: issuerError,
    data: issuerData,
  } = useQuery(IssuerList);

  if (loading || issuerLoading) {
    return <LoadingState />;
  }

  if (error || issuerError) {
    return <ErrorHandler error={error} log="FormAssessmentTemplate" />;
  }

  const handleAssessmentSelect = (value, { children }) => {
    form.setFieldsValue({ name: children });
  };

  const assessments =
    currentAssessment &&
    !data.assessmentList.some((el) => el.id === currentAssessment.id)
      ? [currentAssessment].concat(data.assessmentList)
      : data.assessmentList;

  const issuersIdsList = new Set(issuerData.issuerList.map((d) => d.id));
  const issuers = [
    ...issuerData.issuerList,
    ...currentIssuers.filter((d) => !issuersIdsList.has(d.id)),
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Form.Item label="Active" name="active" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item
        label="Assessment Type"
        name="assessmentId"
        rules={[{ required: true, message: 'Select a document type' }]}
      >
        <Select
          size="large"
          showSearch
          onChange={handleAssessmentSelect}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {assessments.map(({ id, name, category }) => (
            <Select.Option key={id} value={id}>
              {`${name} - ${category}`}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: 'Please enter a item name' }]}
      >
        <Input size="large" />
      </Form.Item>

      <Form.Item
        label="Accepted Issuer(s)"
        name="issuersId"
        rules={[{ required: true, message: 'Please select an issuer' }]}
      >
        <Select
          size="large"
          mode="multiple"
          allowClear
          filterOption={(input, option) => {
            return (
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            );
          }}
        >
          {issuers.map((issuer) => {
            return (
              <Select.Option key={issuer.id} value={issuer.id}>
                {issuer.name}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>

      {showExpiration && <ExpirationTypeSelect form={form} />}

      <Form.Item label="Description" name="description">
        <Input.TextArea />
      </Form.Item>
    </Form>
  );
}

FormAssessmentTemplate.propTypes = {
  initialValues: PropTypes.shape({
    active: PropTypes.bool,
    assessmentId: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  dismiss: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  showExpiration: PropTypes.bool,
  currentAssessment: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
  }),
  currentIssuers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ),
};

FormAssessmentTemplate.defaultProps = {
  showExpiration: false,
};
